import { useEffect, useRef } from 'react';
import { getIsMacOs } from 'shared/utils/get-is-mac-os';

type Modifiers = {
  command?: boolean;
  option?: boolean;
};

export const useModifierKeysDown = (
  key: string,
  callback: (e: KeyboardEvent) => void,
  modifiers: Modifiers = { command: true },
) => {
  const commandPressed = useRef(false);
  const commandTracked = modifiers.command ?? true;
  const optionPressed = useRef(false);
  const optionTracked = modifiers.option ?? false;

  useEffect(() => {
    const isMacOs = getIsMacOs();
    const commandKeys = isMacOs
      ? ['MetaLeft', 'MetaRight']
      : ['ControlLeft', 'ControlRight'];
    const optionKeys = ['AltLeft', 'AltRight'];

    const onKeyDown = (e: KeyboardEvent) => {
      const keyId = e.code || e.key;

      if (commandKeys.includes(keyId)) {
        commandPressed.current = true;
      }
      if (optionKeys.includes(keyId)) {
        optionPressed.current = true;
      }

      if (
        keyId === key &&
        (commandTracked ? commandPressed.current : !commandPressed.current) &&
        (optionTracked ? optionPressed.current : !optionPressed.current)
      ) {
        callback(e);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const keyId = e.code || e.key;

      if (commandKeys.includes(keyId)) {
        commandPressed.current = false;
      }

      if (optionKeys.includes(keyId)) {
        optionPressed.current = false;
      }
    };

    const unregisterKeys = () => {
      commandPressed.current = false;
      optionPressed.current = false;
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', unregisterKeys);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('blur', unregisterKeys);
    };
  }, [callback, commandTracked, key, optionTracked]);
};
